import { useState, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { Loader2, Download, BarChart3, Users, Car, Store, IndianRupee, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { convertToCSV, downloadCSV } from '@/utils/csvExport';

const REPORT_TYPES = [
  { value: 'driver-ledger', label: 'Driver Wise Ledger', icon: Users },
  { value: 'car-ledger', label: 'Car Wise Ledger', icon: Car },
  { value: 'vendor-ledger', label: 'Vendor Wise Ledger', icon: Store },
  { value: 'collection', label: 'Collection Report', icon: IndianRupee },
  { value: 'pending-os', label: 'Pending OS Report', icon: AlertCircle },
  { value: 'hisaab-summary', label: 'Weekly Hisaab Summary', icon: FileSpreadsheet },
];

const Reports = () => {
  const { API } = useAuth();
  const [reportType, setReportType] = useState('');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);

  const generateReport = useCallback(async () => {
    if (!reportType) { toast.error('Select a report type'); return; }
    setLoading(true);
    setReportData(null);
    try {
      const res = await axios.get(`${API}/reports/${reportType}`);
      setReportData({ type: reportType, data: res.data });
    } catch (error) {
      toast.error('Failed to generate report');
    } finally {
      setLoading(false);
    }
  }, [API, reportType]);

  const handleExport = () => {
    if (!reportData) return;
    const { type, data } = reportData;
    let csv = '';
    let filename = `${type}_${new Date().toISOString().split('T')[0]}.csv`;

    if (type === 'driver-ledger') {
      const headers = [{ label: 'Driver ID', key: 'driver_id_ext' }, { label: 'Name', key: 'driver_name' },
        { label: 'Total Received', key: 'total_received' }, { label: 'Total Paid', key: 'total_paid' }, { label: 'Net', key: 'net' }];
      csv = convertToCSV(data, headers);
    } else if (type === 'car-ledger') {
      const headers = [{ label: 'Vehicle', key: 'vehicle_number' }, { label: 'Total Received', key: 'total_received' },
        { label: 'Total Paid', key: 'total_paid' }, { label: 'Net', key: 'net' }];
      csv = convertToCSV(data, headers);
    } else if (type === 'vendor-ledger') {
      const headers = [{ label: 'Vendor', key: 'vendor_name' }, { label: 'Total Received', key: 'total_received' },
        { label: 'Total Paid', key: 'total_paid' }, { label: 'Net', key: 'net' }];
      csv = convertToCSV(data, headers);
    } else if (type === 'collection') {
      const headers = [{ label: 'Month', key: 'month' }, { label: 'Amount', key: 'total' }, { label: 'Count', key: 'count' }];
      csv = convertToCSV(data.monthly || [], headers);
    } else if (type === 'pending-os') {
      const headers = [{ label: 'Driver', key: 'driver_name' }, { label: 'Driver ID', key: 'driver_id_ext' },
        { label: 'Vehicle', key: 'vehicle_number' }, { label: 'Closing OS', key: 'closing_os' }, { label: 'Status', key: 'status' }];
      csv = convertToCSV(data.records || [], headers);
    } else if (type === 'hisaab-summary') {
      const headers = [{ label: 'Week', key: 'week_begin' }, { label: 'Earnings', key: 'total_earnings' },
        { label: 'Lease', key: 'total_lease' }, { label: 'OS', key: 'total_os' }, { label: 'Trips', key: 'total_trips' }, { label: 'Drivers', key: 'driver_count' }];
      csv = convertToCSV(data.weekly_summaries || [], headers);
    }
    if (csv) downloadCSV(csv, filename);
  };

  const renderReport = () => {
    if (!reportData) return null;
    const { type, data } = reportData;

    if (type === 'driver-ledger') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Driver ID</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Name</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Received</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Paid</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Net</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Transactions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((d, i) => (
              <TableRow key={i} className="border-b border-slate-100">
                <TableCell className="font-mono text-xs">{d.driver_id_ext}</TableCell>
                <TableCell className="font-medium text-sm">{d.driver_name}</TableCell>
                <TableCell className="text-emerald-700 text-sm">{d.total_received.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className="text-red-700 text-sm">{d.total_paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className={`font-semibold text-sm ${d.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {d.net.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{d.transactions?.length || 0}</TableCell>
              </TableRow>
            ))}
            {data.length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No data</TableCell></TableRow>}
          </TableBody>
        </Table>
      );
    }

    if (type === 'car-ledger') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Vehicle</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Received</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Paid</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Net</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Transactions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((d, i) => (
              <TableRow key={i} className="border-b border-slate-100">
                <TableCell className="font-mono text-sm">{d.vehicle_number}</TableCell>
                <TableCell className="text-emerald-700">{d.total_received.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className="text-red-700">{d.total_paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className={`font-semibold ${d.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {d.net.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                </TableCell>
                <TableCell className="text-xs text-slate-500">{d.transactions?.length || 0}</TableCell>
              </TableRow>
            ))}
            {data.length === 0 && <TableRow><TableCell colSpan={5} className="text-center py-8 text-slate-400 text-sm">No data</TableCell></TableRow>}
          </TableBody>
        </Table>
      );
    }

    if (type === 'vendor-ledger') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Vendor</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Received</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Paid</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Net</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((d, i) => (
              <TableRow key={i} className="border-b border-slate-100">
                <TableCell className="font-medium text-sm">{d.vendor_name}</TableCell>
                <TableCell className="text-emerald-700">{d.total_received.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className="text-red-700">{d.total_paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className={`font-semibold ${d.net >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                  {d.net.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
                </TableCell>
              </TableRow>
            ))}
            {data.length === 0 && <TableRow><TableCell colSpan={4} className="text-center py-8 text-slate-400 text-sm">No vendor data</TableCell></TableRow>}
          </TableBody>
        </Table>
      );
    }

    if (type === 'collection') {
      return (
        <div>
          <div className="mb-4 p-4 bg-emerald-50 rounded-lg">
            <p className="text-sm text-emerald-800">Total Collected: <span className="font-bold text-lg">
              {(data.total_collected || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </span></p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Month</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Amount</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Entries</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.monthly || []).map((m, i) => (
                <TableRow key={i} className="border-b border-slate-100">
                  <TableCell className="font-medium">{m.month}</TableCell>
                  <TableCell className="font-semibold text-emerald-700">{m.total.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                  <TableCell>{m.count}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }

    if (type === 'pending-os') {
      return (
        <div>
          <div className="mb-4 p-4 bg-red-50 rounded-lg">
            <p className="text-sm text-red-800">Total Pending: <span className="font-bold text-lg">
              {(data.total_pending || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}
            </span> ({data.count || 0} drivers)</p>
          </div>
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50/80">
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Driver</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Driver ID</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Vehicle</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Closing OS</TableHead>
                <TableHead className="text-xs font-semibold uppercase text-slate-500">Status</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(data.records || []).map((r, i) => (
                <TableRow key={i} className="border-b border-slate-100">
                  <TableCell className="font-medium text-sm">{r.driver_name}</TableCell>
                  <TableCell className="font-mono text-xs">{r.driver_id_ext}</TableCell>
                  <TableCell className="font-mono text-xs">{r.vehicle_number}</TableCell>
                  <TableCell className="font-bold text-red-700">{r.closing_os?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                  <TableCell><span className="text-xs bg-red-50 text-red-700 px-2 py-0.5 rounded-full">{r.status}</span></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      );
    }

    if (type === 'hisaab-summary') {
      return (
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/80">
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Week</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Earnings</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Lease</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">OS</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Trips</TableHead>
              <TableHead className="text-xs font-semibold uppercase text-slate-500">Drivers</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data.weekly_summaries || []).map((w, i) => (
              <TableRow key={i} className="border-b border-slate-100">
                <TableCell className="font-medium text-sm">{w.week_begin || 'N/A'}</TableCell>
                <TableCell className="text-emerald-700">{w.total_earnings?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell>{w.total_lease?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell className="font-semibold">{w.total_os?.toLocaleString('en-IN', { style: 'currency', currency: 'INR' })}</TableCell>
                <TableCell>{w.total_trips}</TableCell>
                <TableCell>{w.driver_count}</TableCell>
              </TableRow>
            ))}
            {(data.weekly_summaries || []).length === 0 && <TableRow><TableCell colSpan={6} className="text-center py-8 text-slate-400 text-sm">No hisaab data</TableCell></TableRow>}
          </TableBody>
        </Table>
      );
    }

    return null;
  };

  return (
    <div className="space-y-6" data-testid="reports-section">
      {/* Controls */}
      <Card className="border border-slate-200 shadow-sm">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-slate-900">Reports</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5">
              <p className="text-xs text-slate-500 font-medium">Report Type</p>
              <Select value={reportType} onValueChange={setReportType}>
                <SelectTrigger className="w-[250px]" data-testid="report-type-select">
                  <SelectValue placeholder="Select report..." />
                </SelectTrigger>
                <SelectContent>
                  {REPORT_TYPES.map(rt => (
                    <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button onClick={generateReport} disabled={loading} className="bg-slate-900 text-white hover:bg-slate-800" data-testid="generate-report-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <BarChart3 className="w-4 h-4 mr-2" />}
              Generate
            </Button>
            {reportData && (
              <Button variant="outline" onClick={handleExport} data-testid="export-report-btn">
                <Download className="w-4 h-4 mr-1" /> Export CSV
              </Button>
            )}
          </div>
          {/* Quick links */}
          <div className="flex flex-wrap gap-2 mt-4">
            {REPORT_TYPES.map(rt => {
              const Icon = rt.icon;
              return (
                <Button key={rt.value} size="sm" variant={reportType === rt.value ? 'default' : 'outline'}
                  className={reportType === rt.value ? 'bg-slate-900 text-white' : ''}
                  onClick={() => { setReportType(rt.value); }} data-testid={`quick-${rt.value}`}>
                  <Icon className="w-3.5 h-3.5 mr-1" /> {rt.label}
                </Button>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Report Results */}
      {reportData && (
        <Card className="border border-slate-200 shadow-sm">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-slate-900">
              {REPORT_TYPES.find(r => r.value === reportData.type)?.label || 'Report'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">{renderReport()}</div>
          </CardContent>
        </Card>
      )}

      {!reportData && !loading && (
        <Card className="border border-dashed border-slate-300">
          <CardContent className="py-12 text-center text-slate-400">
            <BarChart3 className="w-10 h-10 mx-auto mb-2 opacity-40" />
            <p className="text-sm">Select a report type and click Generate to view data.</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default Reports;
